import { memo, useCallback, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { ListPlus, Minimize2, PenLine, Sparkles } from 'lucide-react';
import {
  Button,
  Dropdown,
  RichTextEditor,
  exportRichTextToDocx,
  exportRichTextToPdf,
} from '@aimsos/ui';
import { resolveEditorInputFormat } from './chatDocumentEditorUtils.js';

/** Preset chips: instruction sent to concierge / rewrite agent (plain text document + prompt). */
const DOCUMENT_AI_PRESETS = [
  {
    id: 'improve-writing',
    label: 'Improve writing',
    icon: Sparkles,
    instruction:
      'Improve the writing style and clarity of the following document. Return only the revised document text, no preamble.',
  },
  {
    id: 'make-shorter',
    label: 'Make it shorter',
    icon: Minimize2,
    instruction:
      'Make the following document shorter while preserving the meaning. Return only the revised text, no preamble.',
  },
  {
    id: 'fix-grammar',
    label: 'Fix grammar',
    icon: PenLine,
    instruction:
      'Fix grammar and spelling in the following document. Return only the corrected text, no preamble.',
  },
  {
    id: 'add-details',
    label: 'Add details',
    icon: ListPlus,
    instruction:
      'Add useful details and depth to the following document while staying coherent. Return only the expanded text, no preamble.',
  },
];

const DocumentRichEditor = ({
  value,
  contentFormat = 'auto',
  onChange,
  editorKey = 0,
  documentTitle = 'Document',
  documentAiEnabled = false,
  isDocumentAiRewriting = false,
  onDocumentAiInstruction,
  onOpenSaveToDrive,
  /** Contenedor del header del panel: los botones se renderizan aquí vía portal (sin tocar `@aimsos/ui` / toolbar). */
  headerToolbarPortalHost = null,
}) => {
  const inputFormat = resolveEditorInputFormat(value, contentFormat);
  const [isDownloadBusy, setIsDownloadBusy] = useState(false);

  const aiActions = useMemo(() => {
    if (!documentAiEnabled || typeof onDocumentAiInstruction !== 'function') return [];
    return DOCUMENT_AI_PRESETS.map(({ id, label, icon }) => ({ id, label, icon }));
  }, [documentAiEnabled, onDocumentAiInstruction]);

  const handleAiAction = useCallback(
    (actionId, _payload) => {
      const preset = DOCUMENT_AI_PRESETS.find((p) => p.id === actionId);
      if (!preset || typeof onDocumentAiInstruction !== 'function') return;
      void onDocumentAiInstruction(preset.instruction);
    },
    [onDocumentAiInstruction],
  );

  const handleAiPromptApply = useCallback(
    ({ prompt }) => {
      const trimmed = typeof prompt === 'string' ? prompt.trim() : '';
      if (!trimmed || typeof onDocumentAiInstruction !== 'function') return;
      void onDocumentAiInstruction(trimmed);
    },
    [onDocumentAiInstruction],
  );

  const showAiPanel = documentAiEnabled && typeof onDocumentAiInstruction === 'function';

  const handleDownloadPdf = useCallback(
    async (editor) => {
      if (!editor || isDownloadBusy) return;
      setIsDownloadBusy(true);
      try {
        // CT-5305: builds from `editor.getJSON()`, not a re-parsed HTML
        // string — real Word-style headings and full mark support
        // (bold/italic/strike/underline/code/links), not just bold/italic.
        await exportRichTextToPdf(editor, documentTitle);
      } finally {
        setIsDownloadBusy(false);
      }
    },
    [documentTitle, isDownloadBusy],
  );

  const handleDownloadDocx = useCallback(
    async (editor) => {
      if (!editor || isDownloadBusy) return;
      setIsDownloadBusy(true);
      try {
        await exportRichTextToDocx(editor, documentTitle);
      } finally {
        setIsDownloadBusy(false);
      }
    },
    [documentTitle, isDownloadBusy],
  );

  const renderToolbarActions = useCallback(
    ({ editor, disabled }) => {
      if (!headerToolbarPortalHost || !editor) return null;
      return createPortal(
        <div className="flex flex-wrap items-center justify-end gap-2">
          <Dropdown
            label="Download"
            variant="tertiary"
            size="small"
            alignMenu="right"
            menuPosition="bottom"
            usePortal
            disabled={disabled || isDownloadBusy}
          >
            <Dropdown.Item onClick={() => void handleDownloadPdf(editor)}>PDF</Dropdown.Item>
            <Dropdown.Item onClick={() => void handleDownloadDocx(editor)}>Docx</Dropdown.Item>
          </Dropdown>
          {typeof onOpenSaveToDrive === 'function' ? (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              disabled={disabled}
              onClick={() => {
                onOpenSaveToDrive({
                  plainText: editor.getText(),
                  html: editor.getHTML(),
                });
              }}
            >
              Save to drive
            </Button>
          ) : null}
        </div>,
        headerToolbarPortalHost,
      );
    },
    [
      handleDownloadDocx,
      handleDownloadPdf,
      headerToolbarPortalHost,
      isDownloadBusy,
      onOpenSaveToDrive,
    ],
  );

  return (
    <div className="relative min-h-0">
      {isDocumentAiRewriting ? (
        <div
          className="absolute inset-0 z-10 flex items-center justify-center rounded-xl bg-surface/70 backdrop-blur-[1px]"
          aria-busy="true"
          aria-live="polite"
        >
          <Sparkles className="h-8 w-8 animate-pulse text-brand-500" aria-hidden />
        </div>
      ) : null}
      <RichTextEditor
        key={editorKey}
        value={value}
        inputFormat={inputFormat}
        toolbarPreset="medium"
        onChange={onChange}
        disabled={isDocumentAiRewriting}
        aiActions={aiActions}
        onAiAction={showAiPanel ? handleAiAction : undefined}
        onAiPromptApply={showAiPanel ? handleAiPromptApply : undefined}
        renderToolbarActions={headerToolbarPortalHost ? renderToolbarActions : undefined}
      />
    </div>
  );
};

export default memo(DocumentRichEditor);
